/** Conta bancária do creator (resposta de GET /creator/me/bank-accounts) */
export interface CreatorBankAccount {
  id: string;
  bank: string;
  code: string;
  branch: string;
  account: string;
  pix: string;
  active: boolean;
  default: boolean;
  createdAt?: string;
  lastUpdatedAt?: string;
}

/** Payload para PUT /creator/me/bank-accounts/:id */
export interface UpdateCreatorBankAccountPayload {
  bank?: string;
  code?: string;
  branch?: string;
  account?: string;
  pix?: string;
  default?: boolean;
}

/** Payload para POST /creator/me/bank-accounts (criar nova conta) */
export interface CreateCreatorBankAccountPayload {
  bank: string;
  code: string;
  branch: string;
  account: string;
  pix: string;
  default: boolean;
}

/** Snapshot de recebível por mês (GET /creator/me/receivable-snapshots) */
export type ReceivableSnapshotStatus = "WAITING" | "CALCULATED" | "CONCLUDED";

/** Status exibido para o creator (fluxo de recebimento) */
export type ReceivableDisplayStatus =
  | "awaiting_creator_action"
  | "awaiting_receipt"
  | "received";

export interface CreatorReceivableSnapshot {
  paymentMonth: string;
  status: ReceivableSnapshotStatus;
  currency: string;
  grossAmount: number;
  netAmount: number;
  shareAmount: number;
  sharePercentage: number;
  calculatedAt?: string;
  /** URL para visualizar/baixar a NF-e (quando enviada). Em recebimentos acumulados, mesmo para todas as linhas do grupo. */
  nfeFileUrl?: string | null;
  /** URL para visualizar/baixar o comprovante de pagamento (quando pago). Em recebimentos acumulados, mesmo para todas as linhas do grupo. */
  receiptFileUrl?: string | null;
  /** Agrupa linhas de um mesmo recebimento acumulado (mesma NF-e e mesmo comprovante). */
  receivableGroupId?: string | null;
}

/** Deriva o status de exibição a partir do snapshot (e URLs de documentos). */
export function getReceivableDisplayStatus(
  s: CreatorReceivableSnapshot
): ReceivableDisplayStatus {
  if (s.receiptFileUrl) return "received";
  if (s.nfeFileUrl) return "awaiting_receipt";
  return "awaiting_creator_action";
}

export interface GetCreatorReceivableSnapshotsParams {
  page?: number;
  limit?: number;
  /** Período: 6 ou 12 meses */
  periodMonths?: number;
}

/** Resposta paginada de receivable-snapshots */
export interface PaginatedCreatorReceivableSnapshotsResponse {
  data: CreatorReceivableSnapshot[];
  page: number;
  limit: number;
  totalItems: number;
  totalPage?: number;
}

/** Status de pagamento (GET /creator/me/payments) */
export enum CreatorPaymentStatus {
  PAID = "PAID",
  PENDING = "PENDING",
  SCHEDULED = "SCHEDULED",
  ERROR = "ERROR",
}

export interface CreatorPaymentBankAccount {
  bank: string;
  account: string;
  pix: string;
}

export interface CreatorPayment {
  id: string;
  status: CreatorPaymentStatus;
  value: number;
  paymentDate: string;
  referencePaymentDate?: string | null;
  bankAccount?: CreatorPaymentBankAccount | null;
  createdAt: string;
}

export interface GetCreatorPaymentsParams {
  page?: number;
  limit?: number;
  status?: CreatorPaymentStatus | "all";
  search?: string;
}

export interface PaginatedCreatorPaymentsResponse {
  data: CreatorPayment[];
  page: number;
  limit: number;
  totalItems: number;
  totalPage?: number;
}

// --- Solicitações de recebimento (receipt-requests) ---

export type CreatorReceiptRequestStatus =
  | "PENDING"
  | "PROCESSING"
  | "PAID"
  | "REJECTED";

/** Payload para POST /creator/me/receipt-requests */
export interface CreateCreatorReceiptRequestPayload {
  bankAccountId: string;
  paymentMonths: string[];
}

/** Resposta 201 de POST /creator/me/receipt-requests (upload da NF-e é PUT na signedUploadUrl) */
export interface CreateCreatorReceiptRequestResponse {
  id: string;
  status: CreatorReceiptRequestStatus;
  signedUploadUrl: string;
  path: string;
  token: string;
  publicUrl: string;
  paymentMonths: string[];
  createdAt: string;
}

/** Conta bancária resumida em item de listagem de receipt-requests */
export interface CreatorReceiptRequestBankAccount {
  id: string;
  bank: string;
  account: string;
  pix: string;
}

/** Item da lista GET /creator/me/receipt-requests */
export interface CreatorReceiptRequest {
  id: string;
  status: CreatorReceiptRequestStatus;
  paymentMonths: string[];
  totalAmount: number | null;
  nfeFileUrl: string;
  receiptFileUrl: string | null;
  bankAccount: CreatorReceiptRequestBankAccount;
  createdAt: string;
}

export interface GetCreatorReceiptRequestsParams {
  page?: number;
  limit?: number;
}

export interface PaginatedCreatorReceiptRequestsResponse {
  data: CreatorReceiptRequest[];
  page: number;
  limit: number;
  totalItems: number;
  totalPage: number;
}
